import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Item } from "../../../pojo/Item";
import { NameService } from "../../../services/name.service";
import { RoleService } from "../../../services/role.service";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25TriggerRerenderModule } from "../../s25-trigger-rerender/s25.trigger.rerender.module";
import { academicLeafDetail } from "../../academic-curriculum/academic.progress.labels";
import {
    AcademicLeafType,
    CocurricularLeafType,
    RequirementDomain,
    RequirementLeafType,
    type RequirementTreeNode,
} from "./trail.api";
import {
    dropdownItemFromId,
    dropdownItemsFromIds,
    idFromDropdownItem,
    idsFromDropdownItems,
} from "./trail.dropdown.util";
import { UserStore } from "../../../stores/user.store";
import { TrailLabels } from "./trail.labels";
import { formatUserprefDate, toDateTimeLocalValue } from "./trail.userpref.date";

export type RequirementGroupType = "ALL" | "ANY" | "N_OF" | "MIN_CREDITS";

export interface RequirementLeafNode {
    nodeType: "requirement";
    leafType: RequirementLeafType;
    label: string;
    name?: string;
    requirementId?: number;
    threshold?: number;
    referenceId?: number;
    referenceKey?: string;
    eventMatchMode?: "ANY" | "ALL" | "N_OF";
    orgScope?: "ALL_ORGS" | "SPECIFIC_ORGS";
    orgRoleMatchMode?: "ANY" | "ALL" | "N_OF";
    nRequiredRoles?: number;
    nRequiredEvents?: number;
    roleIds?: number[];
    orgIds?: number[];
    deadlineStartDt?: string;
    deadlineEndDt?: string;
    includedRootGroupId?: number;
    includedRootByCohortId?: Record<number, number>;
    courseId?: number;
    minGrade?: string;
    isCorequisite?: boolean;
    disallowPassNoPass?: boolean;
    isProhibited?: boolean;
    subject?: string;
    levelMin?: number;
    levelMax?: number;
    externalCode?: string;
    requiredValue?: string;
    minNumericValue?: number;
    excludeCourseIds?: number[];
}

export interface IncludeTreeRootOption {
    rootGroupId: number;
    name: string;
    code?: string;
}

export interface IncludeTreeCohortOption {
    cohortId: number;
    cohortCode: string;
    roots: IncludeTreeRootOption[];
}

export interface RequirementGroupNode {
    nodeType: "group";
    groupType: RequirementGroupType;
    label: string;
    name?: string;
    groupId?: number;
    groupKey?: string;
    nOfThreshold?: number;
    minCredits?: number;
    maxCredits?: number;
    children: RequirementNode[];
}

export type RequirementNode = RequirementLeafNode | RequirementGroupNode;

export function includedRootIdForCohort(leaf: RequirementLeafNode, cohortId: number): number | undefined {
    return leaf.includedRootByCohortId?.[cohortId];
}

export function includeTreeRootLabel(root: IncludeTreeRootOption): string {
    return `${root.name}${root.code ? ` (${root.code})` : ""}`;
}

export function firstIncludeTreeCode(
    leaf: RequirementLeafNode,
    cohorts: IncludeTreeCohortOption[],
): string | undefined {
    for (const cohort of cohorts) {
        const rootGroupId = includedRootIdForCohort(leaf, cohort.cohortId);
        if (rootGroupId == null) continue;
        const root = cohort.roots.find((option) => option.rootGroupId === rootGroupId);
        if (root?.code) return root.code;
    }
    return undefined;
}

export function includeTreeSelectionsComplete(node: RequirementNode, cohortIds: number[]): boolean {
    if (!cohortIds.length) return false;
    if (node.nodeType === "group") {
        return node.children.every((child) => includeTreeSelectionsComplete(child, cohortIds));
    }
    if (node.leafType !== "INCLUDE_TREE") return true;
    return cohortIds.every((cohortId) => includedRootIdForCohort(node, cohortId) != null);
}

export function includeTreeCohortsHaveRoots(cohorts: IncludeTreeCohortOption[]): boolean {
    return cohorts.length > 0 && cohorts.every((cohort) => cohort.roots.length > 0);
}

export function includeTreeMappingSummary(node: RequirementNode, cohorts: IncludeTreeCohortOption[]): string[] {
    const lines: string[] = [];
    function visit(current: RequirementNode) {
        if (current.nodeType === "group") {
            for (const child of current.children) visit(child);
            return;
        }
        if (current.leafType !== "INCLUDE_TREE") return;
        const leafName = current.name || current.label || "Include Tree";
        const mappings = cohorts.map((cohort) => {
            const rootGroupId = includedRootIdForCohort(current, cohort.cohortId);
            const root = cohort.roots.find((option) => option.rootGroupId === rootGroupId);
            const treeLabel = root ? includeTreeRootLabel(root) : "—";
            return `${cohort.cohortCode} → ${treeLabel}`;
        });
        lines.push(`${leafName}: ${mappings.join("; ")}`);
    }
    visit(node);
    return lines;
}

const DEFAULT_COUNT_THRESHOLD = 1;

function defaultCountThreshold(value: number | undefined): number {
    return value ?? DEFAULT_COUNT_THRESHOLD;
}

export function toCreateRequirementTree(node: RequirementNode): RequirementTreeNode {
    if (node.nodeType === "group") {
        return {
            nodeType: "group",
            groupId: node.groupId,
            groupKey: node.groupKey,
            name: node.name,
            label: node.label,
            groupType: node.groupType,
            nOfThreshold: node.groupType === "N_OF" ? defaultCountThreshold(node.nOfThreshold) : undefined,
            minCredits: node.groupType === "MIN_CREDITS" ? defaultCountThreshold(node.minCredits) : undefined,
            maxCredits: node.maxCredits,
            children: node.children.map((child) => toCreateRequirementTree(child)),
        };
    }
    return {
        nodeType: "requirement",
        name: node.name,
        label: node.label,
        leafType: node.leafType,
        requirementId: node.requirementId,
        referenceId: node.referenceId,
        referenceKey: node.referenceKey,
        threshold: node.leafType === "SERVICE_HOURS" ? defaultCountThreshold(node.threshold) : undefined,
        deadlineStartDt: node.deadlineStartDt,
        deadlineEndDt: node.deadlineEndDt,
        includedRootByCohortId: node.leafType === "INCLUDE_TREE" ? node.includedRootByCohortId : undefined,
        eventMatchMode: node.eventMatchMode,
        orgScope: node.orgScope,
        orgRoleMatchMode: node.orgRoleMatchMode,
        nRequiredRoles:
            node.leafType === "ORG_ROLE" && node.orgRoleMatchMode === "N_OF"
                ? defaultCountThreshold(node.nRequiredRoles)
                : node.nRequiredRoles,
        nRequiredEvents:
            node.leafType === "EVENT_ATTENDANCE" && node.eventMatchMode === "N_OF"
                ? defaultCountThreshold(node.nRequiredEvents)
                : node.nRequiredEvents,
        roleIds: node.roleIds,
        orgIds: node.orgIds,
        courseId: node.courseId,
        minGrade: node.minGrade,
        isCorequisite: node.isCorequisite,
        disallowPassNoPass: node.disallowPassNoPass,
        isProhibited: node.isProhibited,
        subject: node.subject,
        levelMin: node.levelMin,
        levelMax: node.levelMax,
        externalCode: node.externalCode,
        requiredValue: node.requiredValue,
        minNumericValue: node.minNumericValue,
        excludeCourseIds: node.excludeCourseIds,
    };
}

export function seedIncludeTreeFromPersisted(
    includedRootGroupId: number | undefined,
    cohortId: number | undefined,
): Record<number, number> | undefined {
    if (includedRootGroupId == null || cohortId == null) return undefined;
    return { [cohortId]: includedRootGroupId };
}

export function persistedIncludedRootGroupId(
    leaf: RequirementLeafNode,
    cohortId: number | undefined,
): number | undefined {
    if (cohortId != null) {
        return includedRootIdForCohort(leaf, cohortId);
    }
    return leaf.includedRootGroupId;
}

export function groupTypeFromWire(tree: RequirementTreeNode): RequirementGroupType {
    const wire = tree.groupType ?? tree.operator;
    if (wire === "ALL" || wire === "ANY" || wire === "N_OF" || wire === "MIN_CREDITS") {
        return wire;
    }
    return "ALL";
}

export function wireTreeToEditorNode(
    tree: RequirementTreeNode | undefined,
    cohortId: number | undefined,
): RequirementGroupNode {
    if (!tree) {
        return { nodeType: "group", groupType: "ALL", label: "", children: [] };
    }
    const groupType = groupTypeFromWire(tree);
    return {
        nodeType: "group",
        groupType,
        label: tree.name ?? "",
        name: tree.name,
        groupId: tree.groupId,
        groupKey: tree.groupKey,
        nOfThreshold: groupType === "N_OF" ? defaultCountThreshold(tree.nOfThreshold ?? tree.nRequired) : undefined,
        minCredits: groupType === "MIN_CREDITS" ? defaultCountThreshold(tree.minCredits) : undefined,
        maxCredits: tree.maxCredits,
        children: (tree.children ?? []).map((child) => {
            if (child.children?.length || (!child.leafType && !child.includedRootGroupId)) {
                return wireTreeToEditorNode(child, cohortId);
            }
            const leafType =
                child.leafType ?? (child.includedRootGroupId ? ("INCLUDE_TREE" as const) : ("BADGE" as const));
            return {
                nodeType: "requirement" as const,
                leafType,
                label: child.name || child.label || "",
                name: child.name,
                requirementId: child.requirementId ?? child.groupId,
                threshold: leafType === "SERVICE_HOURS" ? defaultCountThreshold(child.threshold) : child.threshold,
                referenceId: child.referenceId,
                referenceKey: child.referenceKey,
                deadlineStartDt: child.deadlineStartDt,
                deadlineEndDt: child.deadlineEndDt,
                courseId: child.courseId,
                minGrade: child.minGrade,
                excludeCourseIds: child.excludeCourseIds,
                isCorequisite: child.isCorequisite,
                disallowPassNoPass: child.disallowPassNoPass,
                isProhibited: child.isProhibited,
                subject: child.subject,
                levelMin: child.levelMin,
                levelMax: child.levelMax,
                externalCode: child.externalCode,
                requiredValue: child.requiredValue,
                minNumericValue: child.minNumericValue,
                includedRootByCohortId: seedIncludeTreeFromPersisted(child.includedRootGroupId, cohortId),
                eventMatchMode: child.eventMatchMode as RequirementLeafNode["eventMatchMode"],
                orgScope: child.orgScope as RequirementLeafNode["orgScope"],
                orgRoleMatchMode: child.orgRoleMatchMode as RequirementLeafNode["orgRoleMatchMode"],
                nRequiredRoles:
                    leafType === "ORG_ROLE" && child.orgRoleMatchMode === "N_OF"
                        ? defaultCountThreshold(child.nRequiredRoles)
                        : child.nRequiredRoles,
                nRequiredEvents:
                    leafType === "EVENT_ATTENDANCE" && child.eventMatchMode === "N_OF"
                        ? defaultCountThreshold(child.nRequiredEvents)
                        : child.nRequiredEvents,
                roleIds: child.roleIds,
                orgIds: child.orgIds,
            };
        }),
    };
}

export function editorNodeToSaveTree(node: RequirementNode, cohortId: number | undefined): RequirementTreeNode {
    if (node.nodeType === "group") {
        return {
            nodeType: "group",
            groupId: node.groupId,
            groupKey: node.groupKey,
            name: node.name,
            label: node.label,
            groupType: node.groupType,
            operator: node.groupType,
            nOfThreshold: node.groupType === "N_OF" ? defaultCountThreshold(node.nOfThreshold) : undefined,
            nRequired: node.groupType === "N_OF" ? defaultCountThreshold(node.nOfThreshold) : undefined,
            minCredits: node.groupType === "MIN_CREDITS" ? defaultCountThreshold(node.minCredits) : undefined,
            maxCredits: node.maxCredits,
            children: node.children.map((child) => editorNodeToSaveTree(child, cohortId)),
        };
    }
    return {
        nodeType: "requirement",
        name: node.name,
        label: node.label,
        leafType: node.leafType,
        requirementId: node.requirementId,
        referenceId: node.referenceId,
        referenceKey: node.referenceKey,
        threshold: node.leafType === "SERVICE_HOURS" ? defaultCountThreshold(node.threshold) : undefined,
        deadlineStartDt: node.deadlineStartDt,
        deadlineEndDt: node.deadlineEndDt,
        includedRootGroupId:
            node.leafType === "INCLUDE_TREE" ? persistedIncludedRootGroupId(node, cohortId) : undefined,
        eventMatchMode: node.eventMatchMode,
        orgScope: node.orgScope,
        orgRoleMatchMode: node.orgRoleMatchMode,
        nRequiredRoles:
            node.leafType === "ORG_ROLE" && node.orgRoleMatchMode === "N_OF"
                ? defaultCountThreshold(node.nRequiredRoles)
                : node.nRequiredRoles,
        nRequiredEvents:
            node.leafType === "EVENT_ATTENDANCE" && node.eventMatchMode === "N_OF"
                ? defaultCountThreshold(node.nRequiredEvents)
                : node.nRequiredEvents,
        roleIds: node.roleIds,
        orgIds: node.orgIds,
        courseId: node.courseId,
        minGrade: node.minGrade,
        isCorequisite: node.isCorequisite,
        disallowPassNoPass: node.disallowPassNoPass,
        isProhibited: node.isProhibited,
        subject: node.subject,
        levelMin: node.levelMin,
        levelMax: node.levelMax,
        externalCode: node.externalCode,
        requiredValue: node.requiredValue,
        minNumericValue: node.minNumericValue,
        excludeCourseIds: node.excludeCourseIds,
    };
}

export interface RequirementReferenceNames {
    searchById: Record<number, string>;
    roleById: Record<number, string>;
    orgById: Record<number, string>;
}

interface ReferenceNameIds {
    searchIds: number[];
    roleIds: number[];
    orgIds: number[];
}

const EMPTY_REFERENCE_NAMES: RequirementReferenceNames = {
    searchById: {},
    roleById: {},
    orgById: {},
};

function uniqueSortedIds(ids: Iterable<number>): number[] {
    return [...new Set(ids)].sort((left, right) => left - right);
}

function collectReferenceIds(node: RequirementNode): ReferenceNameIds {
    const searchIds: number[] = [];
    const roleIds: number[] = [];
    const orgIds: number[] = [];

    function visit(current: RequirementNode) {
        if (current.nodeType === "group") {
            for (const child of current.children) {
                visit(child);
            }
            return;
        }
        if (current.leafType === "EVENT_ATTENDANCE" && current.referenceId) {
            searchIds.push(current.referenceId);
        }
        if (current.leafType === "ORG_ROLE") {
            if (current.roleIds?.length) {
                roleIds.push(...current.roleIds);
            }
            if (current.orgScope === "SPECIFIC_ORGS" && current.orgIds?.length) {
                orgIds.push(...current.orgIds);
            }
        }
    }

    visit(node);
    return {
        searchIds: uniqueSortedIds(searchIds),
        roleIds: uniqueSortedIds(roleIds),
        orgIds: uniqueSortedIds(orgIds),
    };
}

function hasReferenceIds(ids: ReferenceNameIds): boolean {
    return ids.searchIds.length > 0 || ids.roleIds.length > 0 || ids.orgIds.length > 0;
}

async function loadReferenceNames(ids: ReferenceNameIds): Promise<RequirementReferenceNames> {
    const [searches, roles, orgNameByKey] = await Promise.all([
        ids.searchIds.length ? SearchCriteriaService.getEventSearches() : [],
        ids.roleIds.length ? RoleService.getRoles(Item.Ids.Organization) : [],
        ids.orgIds.length
            ? NameService.getNames(ids.orgIds.map((orgId) => ({ itemId: orgId, itemTypeId: Item.Ids.Organization })))
            : Promise.resolve({} as Record<string, string>),
    ]);

    const searchById: Record<number, string> = {};
    for (const search of searches ?? []) {
        const searchId = Number(search.itemId);
        if (ids.searchIds.includes(searchId) && search.itemName) {
            searchById[searchId] = search.itemName;
        }
    }

    const roleById: Record<number, string> = {};
    for (const role of roles ?? []) {
        if (ids.roleIds.includes(role.role_id) && role.role_name) {
            roleById[role.role_id] = role.role_name;
        }
    }

    const orgById: Record<number, string> = {};
    for (const orgId of ids.orgIds) {
        const orgName = orgNameByKey[NameService.itemKeyFromIds(orgId, Item.Ids.Organization)];
        if (orgName) {
            orgById[orgId] = orgName;
        }
    }

    return { searchById, roleById, orgById };
}

function joinedNames(ids: number[] | undefined, nameById: Record<number, string>): string | undefined {
    if (!ids?.length) return undefined;
    const names = ids.map((id) => nameById[id]).filter((name): name is string => !!name);
    return names.length ? names.join(", ") : undefined;
}

export interface RequirementCourseOption {
    courseId: number;
    courseCode: string;
    title?: string;
}

const COCURRICULAR_LEAF_TYPE_OPTIONS: { value: CocurricularLeafType; label: string }[] = [
    { value: "BADGE", label: "Badge" },
    { value: "EVENT_ATTENDANCE", label: "Event Attendance" },
    { value: "SERVICE_HOURS", label: "Service Hours" },
    { value: "EXPERIENCE", label: TrailLabels.EXPERIENCE },
    { value: "TRAIL_SUMMIT", label: TrailLabels.SUMMIT },
    { value: "APPROVAL", label: "Approval" },
    { value: "ORG_ROLE", label: "Organization Role" },
    { value: "INCLUDE_TREE", label: "Include Tree" },
];

const ACADEMIC_LEAF_TYPE_OPTIONS: { value: AcademicLeafType; label: string }[] = [
    { value: "COURSE", label: "Course" },
    { value: "COURSE_RULE", label: "Course rule" },
    { value: "EXTERNAL", label: "External requirement" },
];

const GROUP_TYPE_OPTIONS: { value: RequirementGroupType; label: string }[] = [
    { value: "ALL", label: "All of" },
    { value: "ANY", label: "Any of" },
    { value: "N_OF", label: "N of" },
    { value: "MIN_CREDITS", label: "Min credits" },
];

@Component({
    selector: "s25-ng-trail-requirement-tree",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25ButtonComponent, S25DropdownPaginatedModule, S25TriggerRerenderModule],
    host: { class: "trail-requirement-tree" },
    template: `
        @if (node().nodeType === "group") {
            <div class="trail-requirement-tree__group" [class.trail-requirement-tree__group--nested]="depth() > 0">
                <div class="trail-requirement-tree__group-header">
                    @if (editable()) {
                        <select
                            class="c-input trail-requirement-tree__group-type-select"
                            [ngModel]="asGroup(node()).groupType"
                            (ngModelChange)="onGroupTypeChange($event)"
                        >
                            @for (option of groupTypeOptions; track option.value) {
                                <option [value]="option.value">{{ option.label }}</option>
                            }
                        </select>
                        @if (asGroup(node()).groupType === "N_OF") {
                            <input
                                type="number"
                                class="c-input trail-requirement-tree__n-of-input"
                                [ngModel]="asGroup(node()).nOfThreshold"
                                (ngModelChange)="onNOfThresholdChange($event)"
                                min="1"
                            />
                        }
                        @if (asGroup(node()).groupType === "MIN_CREDITS") {
                            <input
                                type="number"
                                class="c-input trail-requirement-tree__min-credits-input"
                                [ngModel]="asGroup(node()).minCredits"
                                (ngModelChange)="onMinCreditsChange($event)"
                                min="0"
                                step="0.01"
                            />
                        }
                        <input
                            class="c-input trail-requirement-tree__label-input"
                            [ngModel]="asGroup(node()).name ?? ''"
                            (ngModelChange)="onNameChange($event)"
                            placeholder="Group name"
                        />
                    } @else {
                        <span class="trail-requirement-tree__group-type-badge">
                            {{ asGroup(node()).groupType }}
                            @if (asGroup(node()).groupType === "N_OF") {
                                ({{ asGroup(node()).nOfThreshold }})
                            }
                            @if (asGroup(node()).groupType === "MIN_CREDITS") {
                                ({{ asGroup(node()).minCredits }})
                            }
                        </span>
                        <span class="trail-requirement-tree__group-label">{{
                            asGroup(node()).name || node().label
                        }}</span>
                    }
                    @if (editable() && depth() > 0) {
                        <s25-ng-button [type]="'danger--outline'" (clicked)="removeNode.emit()">Remove</s25-ng-button>
                    }
                </div>
                <div class="trail-requirement-tree__children">
                    @for (child of asGroup(node()).children; track childTrackFn($index, child)) {
                        <s25-ng-trail-requirement-tree
                            [node]="child"
                            [depth]="depth() + 1"
                            [editable]="editable()"
                            [domain]="domain()"
                            [badges]="badges()"
                            [trails]="trails()"
                            [courses]="courses()"
                            [includeTreeCohorts]="includeTreeCohorts()"
                            [referenceNames]="resolvedNames()"
                            (nodeChange)="onChildChange($index, $event)"
                            (removeNode)="onRemoveChild($index)"
                        />
                    }
                </div>
                @if (editable()) {
                    <div class="trail-requirement-tree__add-actions">
                        <s25-ng-button [type]="'outline'" (clicked)="addChildGroup()">+ Group</s25-ng-button>
                        <s25-ng-button [type]="'outline'" (clicked)="addChildLeaf()"
                            >+ {{ labels.REQUIREMENT }}</s25-ng-button
                        >
                    </div>
                }
            </div>
        } @else {
            <div class="trail-requirement-tree__leaf">
                @if (editable()) {
                    <select
                        class="c-input trail-requirement-tree__leaf-type-select"
                        [ngModel]="asLeaf(node()).leafType"
                        (ngModelChange)="onLeafTypeChange($event)"
                    >
                        @for (option of leafTypeOptions(); track option.value) {
                            <option [value]="option.value">{{ option.label }}</option>
                        }
                    </select>
                    <input
                        class="c-input trail-requirement-tree__label-input"
                        [ngModel]="asLeaf(node()).name ?? ''"
                        (ngModelChange)="onNameChange($event)"
                        placeholder="{{ labels.REQUIREMENT }} name"
                    />
                    @if (leafNeedsThreshold(asLeaf(node()).leafType)) {
                        <input
                            type="number"
                            class="c-input trail-requirement-tree__threshold-input"
                            [ngModel]="asLeaf(node()).threshold"
                            (ngModelChange)="onThresholdChange($event)"
                            min="1"
                            placeholder="Threshold"
                        />
                    }
                    <s25-ng-button [type]="'danger--outline'" (clicked)="removeNode.emit()">Remove</s25-ng-button>
                    <div class="trail-requirement-tree__leaf-detail">
                        @switch (asLeaf(node()).leafType) {
                            @case ("BADGE") {
                                <label
                                    >Badge
                                    <select
                                        class="c-input"
                                        [ngModel]="asLeaf(node()).referenceId"
                                        (ngModelChange)="onReferenceIdChange($event)"
                                    >
                                        <option [ngValue]="undefined">-- Select --</option>
                                        @for (badge of badges(); track badge.badgeId) {
                                            <option [ngValue]="badge.badgeId">{{ badge.name }}</option>
                                        }
                                    </select>
                                </label>
                            }
                            @case ("EVENT_ATTENDANCE") {
                                <ng-container *s25-ng-trigger-rerender="leafPickerKey(asLeaf(node()))">
                                    <label
                                        >Saved search
                                        <s25-ng-dropdown-search-criteria
                                            class="c-input trail-requirement-tree__event-search-dropdown"
                                            [type]="'eventSearches'"
                                            [value]="dropdownItemFromId(asLeaf(node()).referenceId)"
                                            (valueChange)="onReferenceDropdownChange($event)"
                                            [placeholder]="'Select saved search'"
                                            [ariaLabel]="'Saved search'"
                                        />
                                    </label>
                                </ng-container>
                                <label
                                    >Match Mode
                                    <select
                                        class="c-input"
                                        [ngModel]="asLeaf(node()).eventMatchMode ?? 'ANY'"
                                        (ngModelChange)="onEventMatchModeChange($event)"
                                    >
                                        <option value="ANY">Any</option>
                                        <option value="ALL">All</option>
                                        <option value="N_OF">N of</option>
                                    </select>
                                </label>
                                @if (asLeaf(node()).eventMatchMode === "N_OF") {
                                    <label
                                        >Required Events
                                        <input
                                            type="number"
                                            class="c-input"
                                            [ngModel]="asLeaf(node()).nRequiredEvents"
                                            (ngModelChange)="onNRequiredEventsChange($event)"
                                            min="1"
                                        />
                                    </label>
                                }
                            }
                            @case ("SERVICE_HOURS") {
                                <ng-container *s25-ng-trigger-rerender="leafPickerKey(asLeaf(node()))">
                                    <label
                                        >Organization (optional)
                                        <s25-ng-dropdown-search-criteria
                                            class="c-input trail-requirement-tree__org-dropdown"
                                            [type]="'studentEngagementOrganizations'"
                                            [hasNoneItem]="true"
                                            [value]="dropdownItemFromId(asLeaf(node()).referenceId)"
                                            (valueChange)="onReferenceDropdownChange($event)"
                                            [placeholder]="'Any organization'"
                                            [ariaLabel]="'Organization'"
                                        />
                                    </label>
                                </ng-container>
                            }
                            @case ("TRAIL_SUMMIT") {
                                <label
                                    >{{ labels.TRAIL }}
                                    <select
                                        class="c-input"
                                        [ngModel]="asLeaf(node()).referenceId"
                                        (ngModelChange)="onReferenceIdChange($event)"
                                    >
                                        <option [ngValue]="undefined">-- Select --</option>
                                        @for (trail of trails(); track trail.trailId) {
                                            <option [ngValue]="trail.trailId">{{ trail.name }}</option>
                                        }
                                    </select>
                                </label>
                            }
                            @case ("ORG_ROLE") {
                                <label
                                    >Org Scope
                                    <select
                                        class="c-input"
                                        [ngModel]="asLeaf(node()).orgScope ?? 'ALL_ORGS'"
                                        (ngModelChange)="onOrgScopeChange($event)"
                                    >
                                        <option value="ALL_ORGS">All Orgs</option>
                                        <option value="SPECIFIC_ORGS">Specific Orgs</option>
                                    </select>
                                </label>
                                <label
                                    >Role Match
                                    <select
                                        class="c-input"
                                        [ngModel]="asLeaf(node()).orgRoleMatchMode ?? 'ANY'"
                                        (ngModelChange)="onOrgRoleMatchModeChange($event)"
                                    >
                                        <option value="ANY">Any</option>
                                        <option value="ALL">All</option>
                                        <option value="N_OF">N of</option>
                                    </select>
                                </label>
                                @if (asLeaf(node()).orgRoleMatchMode === "N_OF") {
                                    <label
                                        >Required Roles Threshold
                                        <input
                                            type="number"
                                            class="c-input"
                                            [ngModel]="asLeaf(node()).nRequiredRoles"
                                            (ngModelChange)="onNRequiredRolesChange($event)"
                                            min="1"
                                        />
                                    </label>
                                }
                                <ng-container *s25-ng-trigger-rerender="leafPickerKey(asLeaf(node()))">
                                    <label
                                        >Roles
                                        <s25-ng-dropdown-multi-search-criteria
                                            class="trail-requirement-tree__role-dropdown"
                                            [type]="'organizationRoles'"
                                            [value]="dropdownItemsFromIds(asLeaf(node()).roleIds)"
                                            (valueChange)="onRoleIdsChange($event)"
                                            [placeholder]="'Select roles'"
                                            [hasDone]="false"
                                            [ariaLabel]="'Select organization roles'"
                                        />
                                    </label>
                                    @if (asLeaf(node()).orgScope === "SPECIFIC_ORGS") {
                                        <label
                                            >Organizations
                                            <s25-ng-dropdown-multi-search-criteria
                                                class="trail-requirement-tree__orgs-multi-dropdown"
                                                [type]="'studentEngagementOrganizations'"
                                                [value]="dropdownItemsFromIds(asLeaf(node()).orgIds)"
                                                (valueChange)="onOrgIdsChange($event)"
                                                [placeholder]="'Select organizations'"
                                                [hasDone]="false"
                                                [ariaLabel]="'Select organizations'"
                                            />
                                        </label>
                                    }
                                </ng-container>
                            }
                            @case ("COURSE") {
                                <label
                                    >Course
                                    <select
                                        class="c-input"
                                        data-id="academic-course"
                                        [ngModel]="asLeaf(node()).courseId"
                                        (ngModelChange)="onCourseIdChange($event)"
                                    >
                                        <option [ngValue]="undefined">-- Select --</option>
                                        @for (course of courses(); track course.courseId) {
                                            <option [ngValue]="course.courseId">{{ courseOptionLabel(course) }}</option>
                                        }
                                    </select>
                                </label>
                                <label
                                    >Minimum grade
                                    <input
                                        class="c-input"
                                        data-id="academic-min-grade"
                                        [ngModel]="asLeaf(node()).minGrade ?? ''"
                                        (ngModelChange)="onMinGradeChange($event)"
                                    />
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        data-id="academic-corequisite"
                                        [ngModel]="asLeaf(node()).isCorequisite ?? false"
                                        (ngModelChange)="onCorequisiteChange($event)"
                                    />
                                    Corequisite
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        data-id="academic-disallow-pass-no-pass"
                                        [ngModel]="asLeaf(node()).disallowPassNoPass ?? false"
                                        (ngModelChange)="onDisallowPassNoPassChange($event)"
                                    />
                                    Cannot be taken pass/no-pass
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        data-id="academic-prohibited"
                                        [ngModel]="asLeaf(node()).isProhibited ?? false"
                                        (ngModelChange)="onProhibitedChange($event)"
                                    />
                                    Prohibited
                                </label>
                            }
                            @case ("COURSE_RULE") {
                                <label
                                    >Subject
                                    <input
                                        class="c-input"
                                        data-id="academic-subject"
                                        [ngModel]="asLeaf(node()).subject ?? ''"
                                        (ngModelChange)="onSubjectChange($event)"
                                    />
                                </label>
                                <label
                                    >Level from
                                    <input
                                        type="number"
                                        class="c-input"
                                        data-id="academic-level-min"
                                        [ngModel]="asLeaf(node()).levelMin"
                                        (ngModelChange)="onLevelMinChange($event)"
                                    />
                                </label>
                                <label
                                    >Level to
                                    <input
                                        type="number"
                                        class="c-input"
                                        data-id="academic-level-max"
                                        [ngModel]="asLeaf(node()).levelMax"
                                        (ngModelChange)="onLevelMaxChange($event)"
                                    />
                                </label>
                                <label
                                    >Minimum grade
                                    <input
                                        class="c-input"
                                        data-id="academic-min-grade"
                                        [ngModel]="asLeaf(node()).minGrade ?? ''"
                                        (ngModelChange)="onMinGradeChange($event)"
                                    />
                                </label>
                                <label>
                                    <input
                                        type="checkbox"
                                        data-id="academic-disallow-pass-no-pass"
                                        [ngModel]="asLeaf(node()).disallowPassNoPass ?? false"
                                        (ngModelChange)="onDisallowPassNoPassChange($event)"
                                    />
                                    Cannot be taken pass/no-pass
                                </label>
                            }
                            @case ("EXTERNAL") {
                                <label
                                    >Code
                                    <input
                                        class="c-input"
                                        data-id="academic-external-code"
                                        [ngModel]="asLeaf(node()).externalCode ?? ''"
                                        (ngModelChange)="onExternalCodeChange($event)"
                                    />
                                </label>
                                <label
                                    >Required value
                                    <input
                                        class="c-input"
                                        data-id="academic-required-value"
                                        [ngModel]="asLeaf(node()).requiredValue ?? ''"
                                        (ngModelChange)="onRequiredValueChange($event)"
                                    />
                                </label>
                                <label
                                    >Minimum value
                                    <input
                                        type="number"
                                        class="c-input"
                                        data-id="academic-min-numeric"
                                        [ngModel]="asLeaf(node()).minNumericValue"
                                        (ngModelChange)="onMinNumericValueChange($event)"
                                        step="0.01"
                                    />
                                </label>
                            }
                            @case ("INCLUDE_TREE") {
                                <div class="trail-requirement-tree__include-picker">
                                    <span class="trail-requirement-tree__include-legend">General tree per cohort</span>
                                    <div class="trail-requirement-tree__include-list" data-id="include-tree-list">
                                        @for (cohort of includeTreeCohorts(); track cohort.cohortId) {
                                            <div class="trail-requirement-tree__include-row">
                                                <span
                                                    class="trail-requirement-tree__include-cohort"
                                                    [title]="cohort.cohortCode"
                                                    >{{ cohort.cohortCode }}</span
                                                >
                                                @if (cohort.roots.length) {
                                                    <select
                                                        class="c-input"
                                                        [attr.data-id]="'include-tree-select-' + cohort.cohortId"
                                                        [ngModel]="
                                                            includedRootIdForCohort(asLeaf(node()), cohort.cohortId)
                                                        "
                                                        (ngModelChange)="
                                                            onIncludedRootForCohortChange(cohort.cohortId, $event)
                                                        "
                                                    >
                                                        <option [ngValue]="undefined">-- Select --</option>
                                                        @for (root of cohort.roots; track root.rootGroupId) {
                                                            <option [ngValue]="root.rootGroupId">
                                                                {{ includeTreeRootLabel(root) }}
                                                            </option>
                                                        }
                                                    </select>
                                                } @else {
                                                    <span class="trail-requirement-tree__include-empty"
                                                        >No general trees</span
                                                    >
                                                }
                                            </div>
                                        }
                                    </div>
                                    @if (canMatchIncludeTreeByCode()) {
                                        <s25-ng-button
                                            [type]="'outline'"
                                            data-id="include-tree-match-code"
                                            (clicked)="matchIncludeTreeByCode()"
                                        >
                                            Match by code
                                        </s25-ng-button>
                                    }
                                </div>
                            }
                        }
                    </div>
                    <div class="trail-requirement-tree__deadline-group">
                        <span class="trail-requirement-tree__deadline-label">Start</span>
                        <input
                            type="datetime-local"
                            class="c-input"
                            name="deadlineStart"
                            data-id="deadline-start"
                            [ngModel]="toDateTimeLocalValue(asLeaf(node()).deadlineStartDt)"
                            (ngModelChange)="onDeadlineStartChange($event)"
                        />
                        <span class="trail-requirement-tree__deadline-label">End</span>
                        <input
                            type="datetime-local"
                            class="c-input"
                            name="deadlineEnd"
                            data-id="deadline-end"
                            [ngModel]="toDateTimeLocalValue(asLeaf(node()).deadlineEndDt)"
                            (ngModelChange)="onDeadlineEndChange($event)"
                        />
                    </div>
                } @else {
                    <span class="trail-requirement-tree__leaf-type-badge">{{ asLeaf(node()).leafType }}</span>
                    @if (leafCaption(); as caption) {
                        <span class="trail-requirement-tree__leaf-label">{{ caption }}</span>
                    }
                    @if (asLeaf(node()).threshold) {
                        <span class="trail-requirement-tree__leaf-threshold">({{ asLeaf(node()).threshold }})</span>
                    }
                    @switch (asLeaf(node()).leafType) {
                        @case ("BADGE") {
                            @if (badgeName(asLeaf(node()).referenceId)) {
                                <span class="trail-requirement-tree__leaf-detail-ro"
                                    >Badge: {{ badgeName(asLeaf(node()).referenceId) }}</span
                                >
                            }
                        }
                        @case ("TRAIL_SUMMIT") {
                            @if (trailName(asLeaf(node()).referenceId)) {
                                <span class="trail-requirement-tree__leaf-detail-ro"
                                    >{{ labels.TRAIL }}: {{ trailName(asLeaf(node()).referenceId) }}</span
                                >
                            }
                        }
                        @case ("EVENT_ATTENDANCE") {
                            @if (eventSearchName(asLeaf(node()).referenceId)) {
                                <span class="trail-requirement-tree__leaf-detail-ro">
                                    Search: {{ eventSearchName(asLeaf(node()).referenceId) }}
                                </span>
                            }
                            @if (asLeaf(node()).eventMatchMode) {
                                <span class="trail-requirement-tree__leaf-detail-ro">
                                    Match: {{ asLeaf(node()).eventMatchMode }}
                                    @if (asLeaf(node()).eventMatchMode === "N_OF" && asLeaf(node()).nRequiredEvents) {
                                        ({{ asLeaf(node()).nRequiredEvents }})
                                    }
                                </span>
                            }
                        }
                        @case ("ORG_ROLE") {
                            @if (asLeaf(node()).orgScope) {
                                <span class="trail-requirement-tree__leaf-detail-ro"
                                    >Scope: {{ asLeaf(node()).orgScope }}</span
                                >
                            }
                            @if (orgRoleNames(asLeaf(node()).roleIds)) {
                                <span class="trail-requirement-tree__leaf-detail-ro">
                                    Roles: {{ orgRoleNames(asLeaf(node()).roleIds) }}
                                </span>
                            }
                            @if (asLeaf(node()).orgRoleMatchMode) {
                                <span class="trail-requirement-tree__leaf-detail-ro">
                                    Match: {{ asLeaf(node()).orgRoleMatchMode }}
                                    @if (asLeaf(node()).orgRoleMatchMode === "N_OF" && asLeaf(node()).nRequiredRoles) {
                                        ({{ asLeaf(node()).nRequiredRoles }})
                                    }
                                </span>
                            }
                            @if (asLeaf(node()).orgScope === "SPECIFIC_ORGS" && orgNames(asLeaf(node()).orgIds)) {
                                <span class="trail-requirement-tree__leaf-detail-ro">
                                    Organizations: {{ orgNames(asLeaf(node()).orgIds) }}
                                </span>
                            }
                        }
                        @case ("INCLUDE_TREE") {
                            @if (includeTreeReadOnlyLabel(asLeaf(node()))) {
                                <span class="trail-requirement-tree__leaf-detail-ro"
                                    >Includes: {{ includeTreeReadOnlyLabel(asLeaf(node())) }}</span
                                >
                            }
                        }
                    }
                    @if (academicDetail(); as detail) {
                        <span class="trail-requirement-tree__leaf-detail-ro" data-id="academic-leaf-detail">{{
                            detail
                        }}</span>
                    }
                    @if (asLeaf(node()).deadlineStartDt || asLeaf(node()).deadlineEndDt) {
                        <span class="trail-requirement-tree__leaf-detail-ro">
                            @if (asLeaf(node()).deadlineStartDt) {
                                From: {{ formatDateTime(asLeaf(node()).deadlineStartDt, "") }}
                            }
                            @if (asLeaf(node()).deadlineEndDt) {
                                To: {{ formatDateTime(asLeaf(node()).deadlineEndDt, "") }}
                            }
                        </span>
                    }
                }
            </div>
        }
    `,
    styles: `
        .trail-requirement-tree__group {
            border-left: 3px solid var(--color--border-bold, #ccc);
            padding-left: 1rem;
            margin-bottom: 0.5rem;
        }
        .trail-requirement-tree__group--nested {
            margin-top: 0.5rem;
        }
        .trail-requirement-tree__group-header {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
        }
        .trail-requirement-tree__group-type-select {
            width: 6rem;
        }
        .trail-requirement-tree__n-of-input {
            width: 4rem;
        }
        .trail-requirement-tree__label-input {
            flex: 1;
            min-width: 8rem;
            max-width: 20rem;
        }
        .trail-requirement-tree__group-type-badge {
            font-weight: 600;
            padding: 0.2rem 0.5rem;
            background: var(--color--surface-2-bg, #eee);
            border-radius: 4px;
        }
        .trail-requirement-tree__children {
            margin-left: 0.5rem;
        }
        .trail-requirement-tree__add-actions {
            display: flex;
            gap: 0.5rem;
            margin-top: 0.5rem;
        }
        .trail-requirement-tree__leaf {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
            padding: 0.35rem 0;
        }
        .trail-requirement-tree__leaf-type-select {
            width: 10rem;
        }
        .trail-requirement-tree__threshold-input {
            width: 5rem;
        }
        .trail-requirement-tree__leaf-type-badge {
            font-size: 0.85em;
            font-weight: 600;
            padding: 0.15rem 0.4rem;
            background: var(--color--surface-2-bg, #eee);
            border-radius: 3px;
        }
        .trail-requirement-tree__leaf-detail {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            align-items: center;
            margin-top: 0.25rem;
        }
        .trail-requirement-tree__leaf-detail select,
        .trail-requirement-tree__leaf-detail input,
        .trail-requirement-tree__leaf-detail s25-ng-dropdown-search-criteria,
        .trail-requirement-tree__leaf-detail s25-ng-dropdown-multi-search-criteria {
            max-width: 18rem;
            min-width: 12rem;
        }
        .trail-requirement-tree__deadline-group {
            display: flex;
            gap: 0.5rem;
            align-items: center;
            margin-top: 0.25rem;
        }
        .trail-requirement-tree__deadline-group input {
            max-width: 14rem;
        }
        .trail-requirement-tree__deadline-label {
            font-size: 0.78rem;
            color: var(--color--text-muted, #666);
        }
        .trail-requirement-tree__leaf-detail-ro {
            font-size: 0.85em;
            color: var(--color--text-muted, #666);
            margin-left: 0.25rem;
        }
        .trail-requirement-tree__include-picker {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.35rem;
        }
        .trail-requirement-tree__include-legend {
            font-size: 0.85em;
            font-weight: 600;
        }
        .trail-requirement-tree__include-list {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            max-height: 14rem;
            overflow: auto;
            min-width: 18rem;
            max-width: 28rem;
            width: 100%;
        }
        .trail-requirement-tree__include-row {
            display: grid;
            grid-template-columns: 7rem minmax(0, 1fr);
            gap: 0.5rem;
            align-items: center;
        }
        .trail-requirement-tree__include-cohort {
            font-weight: 600;
            font-size: 0.85em;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .trail-requirement-tree__include-empty {
            font-size: 0.85em;
            color: var(--color--text-muted, #666);
        }
        .trail-requirement-tree__include-list select {
            max-width: none;
            min-width: 0;
            width: 100%;
        }
    `,
})
export class TrailRequirementTreeComponent {
    readonly labels = TrailLabels;
    readonly toDateTimeLocalValue = toDateTimeLocalValue;
    private readonly userStore = inject(UserStore);

    formatDateTime(value: string | undefined | null, empty = "—"): string {
        return formatUserprefDate(value, this.userStore.dateTimeFormat(), empty);
    }

    readonly node = input.required<RequirementNode>();
    readonly depth = input<number>(0);
    readonly editable = input<boolean>(true);
    readonly domain = input<RequirementDomain>("COCURRICULAR");
    readonly badges = input<{ badgeId: number; name: string }[]>([]);
    readonly trails = input<{ trailId: number; name: string }[]>([]);
    readonly courses = input<RequirementCourseOption[]>([]);
    readonly includeTreeCohorts = input<IncludeTreeCohortOption[]>([]);
    readonly referenceNames = input<RequirementReferenceNames | undefined>(undefined);

    readonly nodeChange = output<RequirementNode>();
    readonly removeNode = output<void>();

    readonly leafTypeOptions = computed(() =>
        this.domain() === "ACADEMIC" ? ACADEMIC_LEAF_TYPE_OPTIONS : COCURRICULAR_LEAF_TYPE_OPTIONS,
    );
    readonly academicDetail = computed(() => {
        const node = this.node();
        if (node.nodeType !== "requirement") return undefined;
        return academicLeafDetail(node, this.courses());
    });
    /** A stored name. The type string is already on the badge, so it is not repeated here. */
    readonly leafCaption = computed(() => {
        const node = this.node();
        if (node.nodeType !== "requirement") return undefined;
        const leaf = node as RequirementLeafNode;
        const name = leaf.name?.trim();
        if (name && name !== leaf.leafType) return name;
        const label = leaf.label?.trim();
        if (label && label !== leaf.leafType) return label;
        return undefined;
    });
    readonly groupTypeOptions = GROUP_TYPE_OPTIONS;
    readonly dropdownItemFromId = dropdownItemFromId;
    readonly dropdownItemsFromIds = dropdownItemsFromIds;

    private readonly referenceIds = computed(() => {
        if (this.depth() !== 0 || this.editable()) return undefined;
        return collectReferenceIds(this.node());
    });

    private readonly loadedNames = s25Resource({
        if: () => {
            const ids = this.referenceIds();
            return !!ids && hasReferenceIds(ids);
        },
        params: () => this.referenceIds(),
        loader: ({ params }) => loadReferenceNames(params!),
    });

    readonly resolvedNames = computed(() => this.referenceNames() ?? this.loadedNames.value() ?? EMPTY_REFERENCE_NAMES);

    asGroup(node: RequirementNode): RequirementGroupNode {
        return node as RequirementGroupNode;
    }

    asLeaf(node: RequirementNode): RequirementLeafNode {
        return node as RequirementLeafNode;
    }

    leafNeedsThreshold(leafType: RequirementLeafType): boolean {
        return leafType === "SERVICE_HOURS";
    }

    childTrackFn(index: number, _child: RequirementNode): number {
        return index;
    }

    leafPickerKey(leaf: RequirementLeafNode): string {
        return `${leaf.requirementId ?? "new"}-${leaf.leafType}`;
    }

    onGroupTypeChange(groupType: RequirementGroupType) {
        const current = this.asGroup(this.node());
        this.nodeChange.emit({
            ...current,
            groupType,
            nOfThreshold: groupType === "N_OF" ? defaultCountThreshold(current.nOfThreshold) : undefined,
            minCredits: groupType === "MIN_CREDITS" ? defaultCountThreshold(current.minCredits) : undefined,
        });
    }

    onNOfThresholdChange(threshold: number) {
        const current = this.asGroup(this.node());
        this.nodeChange.emit({ ...current, nOfThreshold: threshold });
    }

    onMinCreditsChange(minCredits: number) {
        const current = this.asGroup(this.node());
        this.nodeChange.emit({ ...current, minCredits });
    }

    onNameChange(name: string) {
        this.nodeChange.emit({ ...this.node(), name: name || undefined });
    }

    onLeafTypeChange(leafType: RequirementLeafType) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({
            ...current,
            leafType,
            threshold: leafType === "SERVICE_HOURS" ? defaultCountThreshold(current.threshold) : undefined,
            referenceId: undefined,
            referenceKey: undefined,
            eventMatchMode: undefined,
            nRequiredEvents: undefined,
            orgScope: undefined,
            orgRoleMatchMode: undefined,
            nRequiredRoles: undefined,
            roleIds: undefined,
            orgIds: undefined,
            includedRootGroupId: undefined,
            includedRootByCohortId: undefined,
            courseId: undefined,
            minGrade: undefined,
            isCorequisite: undefined,
            disallowPassNoPass: undefined,
            isProhibited: undefined,
            subject: undefined,
            levelMin: undefined,
            levelMax: undefined,
            externalCode: undefined,
            requiredValue: undefined,
            minNumericValue: undefined,
        });
    }

    onThresholdChange(threshold: number) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, threshold });
    }

    onReferenceIdChange(referenceId: number | null) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, referenceId: referenceId ?? undefined });
    }

    onReferenceDropdownChange(choice: DropDownItem | null) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, referenceId: idFromDropdownItem(choice) });
    }

    onRoleIdsChange(choices: DropDownItem[] | null) {
        const current = this.asLeaf(this.node());
        const roleIds = idsFromDropdownItems(choices);
        this.nodeChange.emit({ ...current, roleIds: roleIds.length ? roleIds : undefined });
    }

    onOrgIdsChange(choices: DropDownItem[] | null) {
        const current = this.asLeaf(this.node());
        const orgIds = idsFromDropdownItems(choices);
        this.nodeChange.emit({ ...current, orgIds: orgIds.length ? orgIds : undefined });
    }

    onEventMatchModeChange(mode: string) {
        const current = this.asLeaf(this.node());
        const eventMatchMode = mode as "ANY" | "ALL" | "N_OF";
        this.nodeChange.emit({
            ...current,
            eventMatchMode,
            nRequiredEvents: eventMatchMode === "N_OF" ? defaultCountThreshold(current.nRequiredEvents) : undefined,
        });
    }

    onOrgScopeChange(scope: string) {
        const current = this.asLeaf(this.node());
        const orgScope = scope as "ALL_ORGS" | "SPECIFIC_ORGS";
        this.nodeChange.emit({
            ...current,
            orgScope,
            orgIds: orgScope === "ALL_ORGS" ? undefined : current.orgIds,
        });
    }

    onOrgRoleMatchModeChange(mode: string) {
        const current = this.asLeaf(this.node());
        const orgRoleMatchMode = mode as "ANY" | "ALL" | "N_OF";
        this.nodeChange.emit({
            ...current,
            orgRoleMatchMode,
            nRequiredRoles: orgRoleMatchMode === "N_OF" ? defaultCountThreshold(current.nRequiredRoles) : undefined,
        });
    }

    onNRequiredRolesChange(count: number) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, nRequiredRoles: count });
    }

    onNRequiredEventsChange(count: number) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, nRequiredEvents: count });
    }

    onDeadlineStartChange(dateStr: string) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, deadlineStartDt: dateStr || undefined });
    }

    onDeadlineEndChange(dateStr: string) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, deadlineEndDt: dateStr || undefined });
    }

    courseOptionLabel(course: RequirementCourseOption): string {
        return course.title ? `${course.courseCode} — ${course.title}` : course.courseCode;
    }

    onCourseIdChange(courseId: number | null) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, courseId: courseId ?? undefined });
    }

    onMinGradeChange(minGrade: string) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, minGrade: minGrade || undefined });
    }

    onCorequisiteChange(isCorequisite: boolean) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, isCorequisite });
    }

    onDisallowPassNoPassChange(disallowPassNoPass: boolean) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, disallowPassNoPass });
    }

    onProhibitedChange(isProhibited: boolean) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, isProhibited });
    }

    onSubjectChange(subject: string) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, subject: subject || undefined });
    }

    onLevelMinChange(levelMin: number | null) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, levelMin: levelMin ?? undefined });
    }

    onLevelMaxChange(levelMax: number | null) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, levelMax: levelMax ?? undefined });
    }

    onExternalCodeChange(externalCode: string) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, externalCode: externalCode || undefined });
    }

    onRequiredValueChange(requiredValue: string) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, requiredValue: requiredValue || undefined });
    }

    onMinNumericValueChange(minNumericValue: number | null) {
        const current = this.asLeaf(this.node());
        this.nodeChange.emit({ ...current, minNumericValue: minNumericValue ?? undefined });
    }

    badgeName(referenceId: number | undefined): string | undefined {
        if (!referenceId) return undefined;
        return this.badges().find((badge) => badge.badgeId === referenceId)?.name;
    }

    trailName(referenceId: number | undefined): string | undefined {
        if (!referenceId) return undefined;
        return this.trails().find((trail) => trail.trailId === referenceId)?.name;
    }

    eventSearchName(referenceId: number | undefined): string | undefined {
        if (!referenceId) return undefined;
        return this.resolvedNames().searchById[referenceId];
    }

    orgRoleNames(roleIds: number[] | undefined): string | undefined {
        return joinedNames(roleIds, this.resolvedNames().roleById);
    }

    orgNames(orgIds: number[] | undefined): string | undefined {
        return joinedNames(orgIds, this.resolvedNames().orgById);
    }

    includeTreeRootLabel(root: IncludeTreeRootOption): string {
        return includeTreeRootLabel(root);
    }

    includedRootIdForCohort(leaf: RequirementLeafNode, cohortId: number): number | undefined {
        return includedRootIdForCohort(leaf, cohortId);
    }

    includeTreeReadOnlyLabel(leaf: RequirementLeafNode): string | undefined {
        const labels: string[] = [];
        for (const cohort of this.includeTreeCohorts()) {
            const rootGroupId = includedRootIdForCohort(leaf, cohort.cohortId);
            if (rootGroupId == null) continue;
            const root = cohort.roots.find((option) => option.rootGroupId === rootGroupId);
            const treeLabel = root ? this.includeTreeRootLabel(root) : String(rootGroupId);
            labels.push(this.includeTreeCohorts().length > 1 ? `${cohort.cohortCode}: ${treeLabel}` : treeLabel);
        }
        return labels.length ? labels.join("; ") : undefined;
    }

    canMatchIncludeTreeByCode(): boolean {
        const cohorts = this.includeTreeCohorts();
        if (cohorts.length < 2) return false;
        const leaf = this.asLeaf(this.node());
        return firstIncludeTreeCode(leaf, cohorts) != null;
    }

    onIncludedRootForCohortChange(cohortId: number, rootGroupId: number | null) {
        const current = this.asLeaf(this.node());
        const nextMap = { ...(current.includedRootByCohortId ?? {}) };
        if (rootGroupId == null) {
            delete nextMap[cohortId];
        } else {
            nextMap[cohortId] = rootGroupId;
        }
        this.nodeChange.emit({
            ...current,
            includedRootGroupId: undefined,
            includedRootByCohortId: Object.keys(nextMap).length ? nextMap : undefined,
        });
    }

    matchIncludeTreeByCode() {
        const cohorts = this.includeTreeCohorts();
        const current = this.asLeaf(this.node());
        const code = firstIncludeTreeCode(current, cohorts);
        if (!code) return;
        const nextMap = { ...(current.includedRootByCohortId ?? {}) };
        for (const cohort of cohorts) {
            const match = cohort.roots.find((root) => root.code === code);
            if (match) {
                nextMap[cohort.cohortId] = match.rootGroupId;
            }
        }
        this.nodeChange.emit({
            ...current,
            includedRootGroupId: undefined,
            includedRootByCohortId: nextMap,
        });
    }

    onChildChange(childIndex: number, updatedChild: RequirementNode) {
        const current = this.asGroup(this.node());
        const updatedChildren = [...current.children];
        updatedChildren[childIndex] = updatedChild;
        this.nodeChange.emit({ ...current, children: updatedChildren });
    }

    onRemoveChild(childIndex: number) {
        const current = this.asGroup(this.node());
        const updatedChildren = current.children.filter((_, idx) => idx !== childIndex);
        this.nodeChange.emit({ ...current, children: updatedChildren });
    }

    addChildGroup() {
        const current = this.asGroup(this.node());
        const newGroup: RequirementGroupNode = {
            nodeType: "group",
            groupType: "ALL",
            label: "",
            children: [],
        };
        this.nodeChange.emit({ ...current, children: [...current.children, newGroup] });
    }

    addChildLeaf() {
        const current = this.asGroup(this.node());
        const newLeaf: RequirementLeafNode = {
            nodeType: "requirement",
            leafType: this.leafTypeOptions()[0].value,
            label: "",
        };
        this.nodeChange.emit({ ...current, children: [...current.children, newLeaf] });
    }
}
